import React from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";
import {
  DEFAULT_APPLICATION_FIELDS,
  DEFAULT_APPLICATION_FORM_TITLE,
  DEFAULT_APPLICATION_MESSAGE,
} from "../../data/applicationDefaults";

const MAX_FIELDS = 20;

function emptyField(index) {
  return {
    id: `field_${index + 1}`,
    label: "",
    placeholder: "",
    required: true,
    style: "paragraph",
  };
}

export default function ApplicationsSection({
  channels,
  roles = [],
  applications,
  setApplications,
  isOpen,
  setIsOpen,
  setHasUnsavedChanges,
}) {
  const fields = applications.fields?.length
    ? applications.fields
    : DEFAULT_APPLICATION_FIELDS;

  const update = (field, value) => {
    setApplications({ ...applications, [field]: value });
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  const updateField = (index, patch) => {
    const next = fields.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    update("fields", next);
  };

  const addField = () => {
    if (fields.length >= MAX_FIELDS) return;
    update("fields", [...fields, emptyField(fields.length)]);
  };

  const removeField = (index) => {
    update(
      "fields",
      fields.filter((_, i) => i !== index),
    );
  };

  const moveField = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    update("fields", next);
  };

  const resetTemplate = () => {
    setApplications({
      ...applications,
      formTitle: DEFAULT_APPLICATION_FORM_TITLE,
      message: DEFAULT_APPLICATION_MESSAGE,
      fields: DEFAULT_APPLICATION_FIELDS.map((field) => ({ ...field })),
    });
  };

  const pageCount = Math.max(1, Math.ceil(fields.length / 5));

  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Let members run <code>/apply</code> (or <code>.apply</code>) to receive
        the form in DMs. Submissions post to your application log with Accept /
        Reject buttons. Accepted applicants get the roles you select and are
        notified by DM.
      </p>

      <label
        className="input-label"
        style={{ display: "flex", gap: "10px", alignItems: "center" }}
      >
        <input
          type="checkbox"
          checked={Boolean(applications.enabled)}
          onChange={(e) => update("enabled", e.target.checked)}
        />
        Enable applications
      </label>

      <label className="input-label" style={{ marginTop: "16px" }}>
        APPLICATION LOG CHANNEL
      </label>
      <ChannelPicker
        channels={channels}
        value={applications.logChannelId || ""}
        onChange={(v) => update("logChannelId", v)}
        types={[0]}
      />

      <label className="input-label" style={{ marginTop: "16px" }}>
        SUPPORT CHANNEL (DM FAIL FALLBACK)
      </label>
      <ChannelPicker
        channels={channels}
        value={applications.supportChannelId || ""}
        onChange={(v) => update("supportChannelId", v)}
        types={[0]}
        emptyLabel="Use ticket panel channel"
      />
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.75rem", marginTop: "8px" }}
      >
        Shown when the bot cannot DM the user (e.g. DMs closed). Defaults to your
        ticket panel channel if unset.
      </p>

      <label className="input-label" style={{ marginTop: "16px" }}>
        ROLES ON ACCEPT
      </label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.75rem", marginBottom: "8px" }}
      >
        When staff click <b>Accept</b> on an application log, these roles are
        given to the applicant (bot must be above them with Manage Roles).
      </p>
      <RolePicker
        roles={roles}
        multiple
        value={applications.acceptRoleIds || []}
        onChange={(ids) => update("acceptRoleIds", ids)}
        placeholder="Select roles..."
      />

      <label className="input-label" style={{ marginTop: "16px" }}>
        FORM TITLE
      </label>
      <input
        type="text"
        value={applications.formTitle || DEFAULT_APPLICATION_FORM_TITLE}
        onChange={(e) => update("formTitle", e.target.value)}
        className="settings-input"
        maxLength={45}
        placeholder="Staff Application"
      />

      <label className="input-label" style={{ marginTop: "16px" }}>
        APPLICATION DM MESSAGE
      </label>
      <textarea
        value={applications.message || ""}
        onChange={(e) => update("message", e.target.value)}
        className="settings-input"
        rows={6}
        maxLength={2000}
        placeholder={DEFAULT_APPLICATION_MESSAGE}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "18px",
          marginBottom: "8px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <label className="input-label" style={{ margin: 0 }}>
          FORM QUESTIONS ({fields.length}/{MAX_FIELDS} · {pageCount} step
          {pageCount === 1 ? "" : "s"})
        </label>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            className="settings-input"
            style={{ width: "auto", cursor: "pointer" }}
            onClick={resetTemplate}
          >
            Reset to staff template
          </button>
          <button
            type="button"
            className="settings-input"
            style={{ width: "auto", cursor: "pointer" }}
            onClick={addField}
            disabled={fields.length >= MAX_FIELDS}
          >
            Add question
          </button>
        </div>
      </div>

      {fields.map((field, index) => (
        <div
          key={`${field.id || "field"}-${index}`}
          style={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "10px",
            padding: "12px",
            marginBottom: "12px",
            background: "rgba(0,0,0,0.15)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
              gap: "8px",
            }}
          >
            <strong style={{ color: "#e8e0d8" }}>Question {index + 1}</strong>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                className="settings-input"
                style={{ width: "auto", cursor: "pointer", padding: "4px 8px" }}
                onClick={() => moveField(index, -1)}
                disabled={index === 0}
              >
                ↑
              </button>
              <button
                type="button"
                className="settings-input"
                style={{ width: "auto", cursor: "pointer", padding: "4px 8px" }}
                onClick={() => moveField(index, 1)}
                disabled={index === fields.length - 1}
              >
                ↓
              </button>
              <button
                type="button"
                className="settings-input"
                style={{ width: "auto", cursor: "pointer", padding: "4px 8px" }}
                onClick={() => removeField(index)}
                disabled={fields.length <= 1}
              >
                Remove
              </button>
            </div>
          </div>

          <label className="input-label">LABEL (max 45 chars)</label>
          <input
            type="text"
            value={field.label || ""}
            onChange={(e) => updateField(index, { label: e.target.value })}
            className="settings-input"
            maxLength={45}
            placeholder="Discord Username"
          />

          <label className="input-label">PLACEHOLDER</label>
          <input
            type="text"
            value={field.placeholder || ""}
            onChange={(e) =>
              updateField(index, { placeholder: e.target.value })
            }
            className="settings-input"
            maxLength={100}
            placeholder="Hint text shown in the form"
          />

          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              marginTop: "10px",
              flexWrap: "wrap",
            }}
          >
            <label style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span className="input-label" style={{ margin: 0 }}>
                STYLE
              </span>
              <select
                value={field.style || "paragraph"}
                onChange={(e) => updateField(index, { style: e.target.value })}
                className="settings-input"
                style={{ width: "auto", minWidth: "140px" }}
              >
                <option value="short">Short answer</option>
                <option value="paragraph">Paragraph</option>
              </select>
            </label>

            <label style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={field.required !== false}
                onChange={(e) =>
                  updateField(index, { required: e.target.checked })
                }
              />
              Required
            </label>
          </div>
        </div>
      ))}
    </SettingsSection>
  );
}
